import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { callService } from '../services';
import type { Call, CallFilters } from '../repositories/CallRepository';
import type { 
  CreateCallInput, 
  UpdateCallInput,
  CallDashboard
} from '../services/CallService';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const callKeys = {
  all: ['calls'] as const,
  lists: () => [...callKeys.all, 'list'] as const,
  list: (filters?: CallFilters) => [...callKeys.lists(), { filters }] as const,
  details: () => [...callKeys.all, 'detail'] as const,
  detail: (id: string) => [...callKeys.details(), id] as const,
  customer: (customerId: string) => [...callKeys.lists(), 'customer', customerId] as const,
  dashboard: (filters?: CallFilters) => [...callKeys.all, 'dashboard', { filters }] as const,
  transcripts: () => [...callKeys.all, 'transcript'] as const,
  transcript: (callId: string) => [...callKeys.transcripts(), callId] as const,
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all calls with optional filtering
 */
export function useCalls(
  filters?: CallFilters,
  options?: UseQueryOptions<Call[], Error>
) {
  return useQuery<Call[], Error>({
    queryKey: callKeys.list(filters),
    queryFn: () => callService.searchCalls(filters || {}),
    staleTime: 2 * 60 * 1000, // 2 minutes (calls update frequently)
    ...options,
  });
}

/**
 * Fetch a single call by ID
 */
export function useCall(
  id: string,
  options?: UseQueryOptions<Call | null, Error>
) {
  return useQuery<Call | null, Error>({
    queryKey: callKeys.detail(id),
    queryFn: async () => {
      // Call service doesn't have a getCall method, access repository directly
      const { repositories } = await import('../repositories');
      return repositories.calls.findById(id);
    },
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Fetch calls for a specific customer
 */
export function useCustomerCalls(
  customerId: string,
  options?: UseQueryOptions<Call[], Error>
) {
  return useQuery<Call[], Error>({
    queryKey: callKeys.customer(customerId),
    queryFn: () => callService.getCustomerCalls(customerId),
    enabled: !!customerId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Fetch call dashboard with analytics
 */
export function useCallDashboard(
  filters?: CallFilters,
  options?: UseQueryOptions<CallDashboard, Error>
) {
  return useQuery<CallDashboard, Error>({
    queryKey: callKeys.dashboard(filters),
    queryFn: () => callService.getCallDashboard(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes (dashboard updates frequently)
    ...options,
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new call
 */
export function useCreateCall(
  options?: UseMutationOptions<Call, Error, CreateCallInput>
) {
  const queryClient = useQueryClient();

  return useMutation<Call, Error, CreateCallInput>({
    mutationFn: (input) => callService.createCall(input),
    onSuccess: (newCall) => {
      // Invalidate all call lists
      queryClient.invalidateQueries({ queryKey: callKeys.lists() });
      
      // Invalidate customer calls if customer_id exists
      if (newCall.customer_id) {
        queryClient.invalidateQueries({ 
          queryKey: callKeys.customer(newCall.customer_id) 
        });
      }
      
      // Invalidate dashboard
      queryClient.invalidateQueries({ queryKey: callKeys.dashboard() });
      
      // Set the new call in cache
      queryClient.setQueryData(callKeys.detail(newCall.id), newCall);
    },
    ...options,
  });
}

/**
 * Update a call
 */
export function useUpdateCall(
  options?: UseMutationOptions<Call, Error, { id: string; input: UpdateCallInput }>
) {
  const queryClient = useQueryClient();

  return useMutation<Call, Error, { id: string; input: UpdateCallInput }>({
    mutationFn: ({ id, input }) => callService.updateCall(id, input),
    onMutate: async ({ id, input }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: callKeys.detail(id) });

      // Snapshot previous value
      const previousCall = queryClient.getQueryData<Call>(callKeys.detail(id));

      // Optimistically update
      if (previousCall) {
        queryClient.setQueryData(callKeys.detail(id), {
          ...previousCall,
          ...input,
        });
      }

      return { previousCall };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      const ctx = context as { previousCall?: Call } | undefined;
      if (ctx?.previousCall) {
        queryClient.setQueryData(callKeys.detail(id), ctx.previousCall);
      }
    },
    onSuccess: (updatedCall) => {
      // Update cache
      queryClient.setQueryData(callKeys.detail(updatedCall.id), updatedCall);
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: callKeys.lists() });
      
      // Invalidate customer calls if customer_id exists
      if (updatedCall.customer_id) {
        queryClient.invalidateQueries({ 
          queryKey: callKeys.customer(updatedCall.customer_id) 
        });
      }
      
      // Invalidate dashboard
      queryClient.invalidateQueries({ queryKey: callKeys.dashboard() });
    },
    ...options,
  });
}

/**
 * Delete a call
 */
export function useDeleteCall(
  options?: UseMutationOptions<void, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id) => callService.deleteCall(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: callKeys.detail(id) });
      
      // Invalidate all lists
      queryClient.invalidateQueries({ queryKey: callKeys.lists() });
      
      // Invalidate dashboard
      queryClient.invalidateQueries({ queryKey: callKeys.dashboard() });
    },
    ...options,
  });
}

/**
 * Analyze a call transcript
 */
export function useAnalyzeTranscript(
  options?: UseMutationOptions<any, Error, { call_id: string; transcript: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { call_id: string; transcript: string }>({
    mutationFn: ({ call_id, transcript }) => 
      callService.analyzeTranscript({ call_id, transcript }),
    onSuccess: (_, { call_id }) => {
      // Invalidate the call to refetch with new analysis
      queryClient.invalidateQueries({ queryKey: callKeys.detail(call_id) });
      
      // Invalidate dashboard
      queryClient.invalidateQueries({ queryKey: callKeys.dashboard() });
    },
    ...options,
  });
}

/**
 * End a call
 */
export function useEndCall(
  options?: UseMutationOptions<Call, Error, { id: string; durationSeconds: number }>
) {
  const queryClient = useQueryClient();

  return useMutation<Call, Error, { id: string; durationSeconds: number }>({
    mutationFn: ({ id, durationSeconds }) => callService.endCall(id, durationSeconds),
    onSuccess: (call) => {
      // Update cache
      queryClient.setQueryData(callKeys.detail(call.id), call);
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: callKeys.lists() });
      
      // Invalidate customer calls if customer_id exists
      if (call.customer_id) {
        queryClient.invalidateQueries({ 
          queryKey: callKeys.customer(call.customer_id) 
        });
      }
      
      // Invalidate dashboard
      queryClient.invalidateQueries({ queryKey: callKeys.dashboard() });
    },
    ...options,
  });
}

/**
 * Fetch call transcript
 */
export function useCallTranscript(
  callId: string,
  options?: UseQueryOptions<any[], Error>
) {
  return useQuery<any[], Error>({
    queryKey: callKeys.transcript(callId),
    queryFn: async () => {
      const { supabase } = await import('../supabase');
      const { data } = await supabase
        .from('call_transcripts')
        .select('*')
        .eq('call_id', callId)
        .order('sequence_number', { ascending: true });
      return data || [];
    },
    enabled: !!callId && (options?.enabled ?? true),
    staleTime: 10 * 60 * 1000, // 10 minutes (transcripts rarely change)
    ...options,
  });
}
